import { CacheFactory, CacheOptions } from "@typings/cachefactory";
import copy from "fast-copy/dist/fast-copy.esm.js";

export class CacheService {

    static $inject = ["$q", "CacheFactory"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly cacheFactory: CacheFactory) {
    }

    createCache(cacheName: string, options?: CacheOptions) {
        //options = options || {};
        //if (!options.storageMode) {
        //    options.storageMode = "sessionStorage";
        //}

        //this.clearAllCaches();

        this.cacheFactory.createCache(cacheName, options/*, { maxAge: 30 * 60 * 1000 }*/);
        this.cacheFactory.createCache(this.getDataRetrievalPromiseCacheName(cacheName), options/*, { maxAge: 30 * 60 * 1000 }*/);
    }

    clearAllCaches() {
        this.cacheFactory.clearAll();
    }

    clearCache(cacheName: string) {
        this.cacheFactory.get(cacheName).removeAll();
        this.cacheFactory.get(this.getDataRetrievalPromiseCacheName(cacheName)).removeAll();
    }

    get<T>(cacheName: string, id: string | number = null): T {
        const dataCache = this.cacheFactory.get(cacheName);
        if (!dataCache) {
            return null;
        }

        var data = dataCache.get<T>(id || "all");
        return data;
    }

    getDataRetrievalPromise<T>(cacheName: string, id: string | number = null): angular.IPromise<T> {
        return this.get<angular.IPromise<T>>(this.getDataRetrievalPromiseCacheName(cacheName), id);
    }

    getDataAsPromise<T>(cacheName: string, id: string = null): angular.IPromise<T> {
        var data = this.get<T>(cacheName, id);
        if (!data) {
            return null;
        }

        var defer = this.$q.defer<T>();
        defer.resolve(data);

        return defer.promise;
    }

    getOrRetrieveData<T>(cacheName: string, dataRetrievalCallback: () => angular.IPromise<T>, invalidate = false, unsafe = false): angular.IPromise<T> {

        if (!invalidate) {
            var data = this.get<T>(cacheName);
            if (data) {
                var defer = this.$q.defer<T>();

                if (!unsafe) {
                    data = copy(data);
                }

                defer.resolve(data);
                return defer.promise;
            }

            // This allows us to return promises of callbacks that have started but are yet to complete
            const promise = this.getDataRetrievalPromise<T>(cacheName);
            if (promise) {
                return promise;
            }
        }

        const dataRetrievalPromise = dataRetrievalCallback();
        this.saveDataRetrievalPromise(cacheName, dataRetrievalPromise);

        return dataRetrievalPromise.then(data => {
            this.save(cacheName, data);

            return copy(data);
        });
    }

    getOrRetrieveItem<C, T>(cacheName: string, dataRetrievalCallback: () => angular.IPromise<T>, transfomToCacheItem: (data: T) => C, transformToData: (cacheItem: C) => T, id: string | number = null, invalidate = false): angular.IPromise<T> {

        const cache = this.get<C>(cacheName, id);

        if (!invalidate && !!cache) {
            var defer = this.$q.defer<T>();
            defer.resolve(transformToData(cache));
            return defer.promise;
        }

        return dataRetrievalCallback().then((data) => {
            this.save(cacheName, transfomToCacheItem(data), id);
            return copy(data);
        });
    }

    save<T>(cacheName: string, data: T, id: string | number = null) {
        const dataCache = this.cacheFactory.get(cacheName);

        dataCache.put(id || "all", data);
    }

    saveDataRetrievalPromise<T>(cacheName: string, promise: angular.IPromise<T>, id: string | number = null) {
        this.save(this.getDataRetrievalPromiseCacheName(cacheName), promise, id);
    }

    addOrUpdateItem<T>(cacheName: string, newItem: T, matchingPredicate: (item1: T, item2: T) => boolean) {
        var cache = this.get<T[]>(cacheName);
        if (!cache) {
            return;
        }
        var index = cache.findIndex(cacheItem => matchingPredicate(cacheItem, newItem));

        if (index < 0) {
            cache.push(newItem);
        }
        else {
            cache[index] = newItem;
        }
    }

    upsertItem<T>(cacheName: string, id: string | number = null, newItem: T, updateCache: (cache: T, newItem: T) => void) {
        const cache = this.get<T>(cacheName, id);
        if (cache) {
            updateCache(cache, newItem);
        } else {
            this.save(cacheName, newItem, id);
        }
    }

    deleteItem<T>(cacheName: string, delItem: T, matchingPredicate: (item1: T, item2: T) => boolean) {
        const cache = this.get<T[]>(cacheName);
        const index = cache.findIndex(cacheItem => matchingPredicate(cacheItem, delItem));

        if (index !== -1) {
            cache.splice(index, 1);
        }
    }

    // --------------

    private getDataRetrievalPromiseCacheName(cacheName: string): string {
        return cacheName + "promise";
    }
}
